import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('agentToken')}` } });

export function NotificationMarquee() {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const load = useCallback(async () => {
    try {
      const r = await axios.get('/api/admin-notifications/agent?limit=20', authH());
      const unread = (r.data.notifications || []).filter(n => !n.readAt);
      // ⭐ Prioritise admin/manual notifications over system "lead assigned" ones.
      // If any human-sent (sentBy !== 'system') notifications exist, only show those
      // in the marquee — lead-assigned ones are lower priority and visible on the page.
      const adminNotifs  = unread.filter(n => n.sentBy !== 'system');
      const systemNotifs = unread.filter(n => n.sentBy === 'system');
      setItems(adminNotifs.length > 0 ? adminNotifs : systemNotifs);
    } catch(e){}
  }, []);
  useEffect(()=>{ load(); const id=setInterval(load,60000); return()=>clearInterval(id); },[load]);
  const dismiss = async n => {
    setDismissed(p=>new Set([...p,n.notificationId]));
    try { await axios.put(`/api/admin-notifications/agent/${n.notificationId}/read`,{},authH()); } catch(e){}
  };
  const visible = items.filter(n=>!dismissed.has(n.notificationId));
  if (visible.length===0) return null;
  // Scale duration so speed stays roughly constant regardless of item count
  const duration = Math.max(20, visible.length * 10);
  const NotifItem = ({ n, keySuffix = '' }) => (
    <span key={n.notificationId + keySuffix} style={{ display:'inline-flex', alignItems:'center', gap:6, color:'#fff', fontSize:13 }}>
      <span style={{ color:'#fff' }} className="notification-message-marquee">{n.message}</span>
      <button
        onClick={e => { e.stopPropagation(); dismiss(n); }}
        title="Dismiss"
        style={{ background:'rgba(255,255,255,0.25)', border:'none', color:'#fff', borderRadius:'50%', width:18, height:18, cursor:'pointer', fontSize:12, lineHeight:'18px', padding:0, flexShrink:0 }}
      >×</button>
    </span>
  );
  return (
    <div style={{ background:'linear-gradient(90deg,#1a56db,#1e429f)', color:'#fff', padding:'10px 0', overflow:'hidden', borderRadius:8, marginBottom:16 }}>
      <div style={{ display:'flex', alignItems:'center' }}>
        <span style={{ flexShrink:0, padding:'0 14px', fontWeight:700, fontSize:13, opacity:0.8, borderRight:'1px solid rgba(255,255,255,0.3)', marginRight:12 }}>📢</span>
        <div style={{ overflow:'hidden', flex:1 }}>
          <div style={{ display:'inline-flex', gap:60, animation:`marquee ${duration}s linear infinite`, whiteSpace:'nowrap' }}>
            {visible.map(n=><NotifItem key={n.notificationId} n={n} />)}
            {visible.map(n=><NotifItem key={n.notificationId+'d'} n={n} keySuffix="d" />)}
          </div>
        </div>
        <div style={{ flexShrink:0, padding:'0 12px', borderLeft:'1px solid rgba(255,255,255,0.3)', marginLeft:12 }}>
          <a href="/agent/notifications" style={{ fontSize:11, color:'rgba(255,255,255,0.85)', textDecoration:'underline', whiteSpace:'nowrap' }}>View all</a>
        </div>
      </div>
      <style>{`@keyframes marquee{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}`}</style>
    </div>
  );
}

function AgentNotifications() {
  const [notifications,setNotifications]=useState([]);
  const [loading,setLoading]=useState(true);
  const [markingAll,setMarkingAll]=useState(false);

  const load=async()=>{
    setLoading(true);
    try{ const r=await axios.get('/api/admin-notifications/agent?limit=100',authH()); setNotifications(r.data.notifications||[]); }
    catch(e){console.error(e);} finally{setLoading(false);}
  };
  useEffect(()=>{load();},[]);

  const markRead=async id=>{
    try{ await axios.put(`/api/admin-notifications/agent/${id}/read`,{},authH());
      setNotifications(p=>p.map(n=>n.notificationId===id?{...n,readAt:new Date().toISOString()}:n));
    }catch(e){}
  };
  const markAllRead=async()=>{
    setMarkingAll(true);
    try{ await axios.put('/api/admin-notifications/agent/read-all',{},authH());
      const now=new Date().toISOString();
      setNotifications(p=>p.map(n=>({...n,readAt:n.readAt||now})));
    }catch(e){} finally{setMarkingAll(false);}
  };

  const unread=notifications.filter(n=>!n.readAt).length;

  return (
    <div style={{ padding:24 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h2 style={{ margin:0, fontSize:22, fontWeight:700, display:'flex', alignItems:'center', gap:10 }}>
            🔔 Notifications
            {unread>0&&<span style={{ background:'#ef4444', color:'#fff', borderRadius:999, fontSize:12, fontWeight:700, padding:'2px 8px' }}>{unread} new</span>}
          </h2>
          <p style={{ margin:'4px 0 0', color:'#6b7280', fontSize:13 }}>Messages from your portal administrator</p>
        </div>
        {unread>0&&<button onClick={markAllRead} disabled={markingAll} style={{ padding:'8px 16px', background:'#f3f4f6', border:'1px solid #d1d5db', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', color:'#374151' }}>{markingAll?'Marking…':`Mark all ${unread} as read`}</button>}
      </div>
      {loading?<div style={{ textAlign:'center', padding:60, color:'#9ca3af' }}>Loading…</div>:
      notifications.length===0?<div style={{ textAlign:'center', padding:60, color:'#9ca3af' }}><div style={{ fontSize:48, marginBottom:12 }}>🔕</div><div style={{ fontSize:16, fontWeight:600 }}>No notifications yet</div><div style={{ fontSize:13, marginTop:4 }}>Messages from admin will appear here.</div></div>:(
        <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
          {notifications.map(n=>{
            const isNew=!n.readAt;
            return (
              <div key={n.notificationId} onClick={()=>{if(isNew)markRead(n.notificationId);}}
                style={{ background:isNew?'#f8faff':'#fff', border:`1px solid ${isNew?'#dbeafe':'#f3f4f6'}`, borderRadius:10, padding:'14px 18px', cursor:isNew?'pointer':'default', display:'flex', alignItems:'flex-start', gap:12 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:isNew?'#1a56db':'transparent', flexShrink:0, marginTop:6 }}/>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:15, color:'#111827', lineHeight:1.5, fontWeight:isNew?600:400 }}>{n.message}</div>
                  <div style={{ fontSize:11, color:'#9ca3af', marginTop:6 }}>{new Date(n.createdAt).toLocaleString()}{n.readAt&&<span style={{ marginLeft:8, color:'#d1d5db' }}>· Read</span>}</div>
                </div>
                {isNew&&<span style={{ fontSize:11, padding:'2px 8px', borderRadius:10, background:'#eff6ff', color:'#1a56db', fontWeight:700, flexShrink:0 }}>New</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
export default AgentNotifications;

